import {
    faCalendar,
    faFilter,
    faSearch,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Badge,
    Button,
    Card,
    Group,
    Pagination,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { DateTime } from "luxon";
import React from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import TeamApi, {
    ITeamSearchOptions,
    ITeamSearchResult,
} from "../../data/api/teamApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { ICompetition } from "../../types/competition/Competition";
import { IVenue } from "../../types/venue/Venue";
import { CenteredLoader } from "../common/loader";
import { AdminPageLayout } from "./common";

// Constants for default search window
const DEFAULT_SEARCH_WINDOW = {
    PAST_DAYS: 14,
    FUTURE_DAYS: 30,
} as const;

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_PAGE_SIZE = 50;

interface TeamSearchPageProps {
    selectedAssociation?: IAssociation | null;
    associations: { [id: string]: IAssociation };
    competitions: { [id: string]: ICompetition };
    venues: { [id: string]: IVenue };
}

interface TeamSearchPageState {
    isLoading: boolean;
    searchOptions: ITeamSearchOptions;
    results: ITeamSearchResult[];
    total: number;
    error?: string;
    searchDebounceTimer?: NodeJS.Timeout;
    searchCompetitions: { [id: string]: ICompetition };
}

const DAYS_OF_WEEK = [
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
    { value: "0", label: "Sunday" },
];

/**
 * Team Search Page - Admin tool for searching teams with advanced filters
 * Prioritizes teams with recent or upcoming games and shows game history
 */
class TeamSearchPage extends React.Component<
    TeamSearchPageProps,
    TeamSearchPageState
> {
    constructor(props: TeamSearchPageProps) {
        super(props);

        // Set default date range using constants
        const now = new Date();
        const pastDate = new Date(now.getTime() - DEFAULT_SEARCH_WINDOW.PAST_DAYS * 24 * 60 * 60 * 1000);
        const futureDate = new Date(now.getTime() + DEFAULT_SEARCH_WINDOW.FUTURE_DAYS * 24 * 60 * 60 * 1000);

        this.state = {
            isLoading: false,
            searchOptions: {
                limit: DEFAULT_PAGE_SIZE,
                offset: 0,
                dateFrom: pastDate,
                dateTo: futureDate,
            },
            results: [],
            total: 0,
            searchCompetitions: {},
        };
    }

    componentDidMount() {
        // Perform initial search if association is selected
        if (this.props.selectedAssociation) {
            this.handleSearch();
        }
    }

    componentWillUnmount() {
        // Clean up debounce timer
        if (this.state.searchDebounceTimer) {
            clearTimeout(this.state.searchDebounceTimer);
        }
    }

    componentDidUpdate(prevProps: TeamSearchPageProps) {
        // If association changes, update search and re-search
        if (
            this.props.selectedAssociation?._id !==
            prevProps.selectedAssociation?._id
        ) {
            // Clear any pending debounce timer
            if (this.state.searchDebounceTimer) {
                clearTimeout(this.state.searchDebounceTimer);
            }

            this.setState(
                {
                    searchOptions: {
                        ...this.state.searchOptions,
                        associationId: this.props.selectedAssociation?._id,
                        offset: 0,
                    },
                    results: [],
                    total: 0,
                    searchDebounceTimer: undefined,
                },
                () => this.handleSearch()
            );
        }
    }

    handleSearch = async () => {
        this.setState({ isLoading: true, error: undefined });

        try {
            const options = { ...this.state.searchOptions };

            // Set association if selected
            if (this.props.selectedAssociation) {
                options.associationId = this.props.selectedAssociation._id;
            }

            const response = await TeamApi.searchTeams(options);

            // Convert competitions array to map
            const competitionsMap: { [id: string]: ICompetition } = {};
            if (response.competitions) {
                response.competitions.forEach((comp) => {
                    competitionsMap[comp._id] = comp;
                });
            }

            this.setState({
                results: response.teams,
                total: response.total,
                searchCompetitions: competitionsMap,
                isLoading: false,
            });
        } catch (error: any) {
            console.error("Error searching teams:", error);
            this.setState({
                error: error.message || "Failed to search teams",
                isLoading: false,
            });
            toast.error("Failed to search teams: " + (error.message || "Unknown error"));
        }
    };

    clearFilters = () => {
        // Reset to default date range using constants
        const now = new Date();
        const pastDate = new Date(now.getTime() - DEFAULT_SEARCH_WINDOW.PAST_DAYS * 24 * 60 * 60 * 1000);
        const futureDate = new Date(now.getTime() + DEFAULT_SEARCH_WINDOW.FUTURE_DAYS * 24 * 60 * 60 * 1000);

        this.setState(
            {
                searchOptions: {
                    limit: DEFAULT_PAGE_SIZE,
                    offset: 0,
                    associationId: this.props.selectedAssociation?._id,
                    dateFrom: pastDate,
                    dateTo: futureDate,
                },
                results: [],
                total: 0,
            },
            () => this.handleSearch()
        );
    };

    updateSearchOption = (key: keyof ITeamSearchOptions, value: any) => {
        this.setState(
            {
                searchOptions: {
                    ...this.state.searchOptions,
                    [key]: value,
                    offset: 0, // Reset pagination
                },
            },
            () => this.debouncedSearch()
        );
    };

    debouncedSearch = () => {
        // Clear existing timer
        if (this.state.searchDebounceTimer) {
            clearTimeout(this.state.searchDebounceTimer);
        }

        // Set new timer using constant
        const timer = setTimeout(() => {
            this.handleSearch();
        }, SEARCH_DEBOUNCE_MS);

        this.setState({ searchDebounceTimer: timer });
    };

    formatDate = (date: Date | string | undefined): string => {
        if (!date) return "-";
        const dt = DateTime.fromISO(date.toString());
        if (!dt.isValid) return "-";
        return dt.toFormat("dd/MM/yyyy");
    };

    formatDateTime = (date: Date | string | undefined): string => {
        if (!date) return "-";
        const dt = DateTime.fromISO(date.toString());
        if (!dt.isValid) return "-";
        return dt.toFormat("dd/MM/yyyy HH:mm");
    };

    getTeamOpponent = (game: any, teamId: string): string => {
        if (!game) return "-";
        const isTeam1 = game.team1 === teamId;
        const opponentId = isTeam1 ? game.team2 : game.team1;
        const opponentName = isTeam1 ? game.team2Name : game.team1Name;
        return opponentName || `Team ${opponentId?.substring(0, 8)}...` || "-";
    };

    getVenueName = (venueId: string): string => {
        const venue = this.props.venues[venueId];
        return venue?.name || "Unknown Venue";
    };

    getCompetitionName = (competitionId: string): string => {
        // Try from search results first (more up-to-date)
        const searchComp = this.state.searchCompetitions[competitionId];
        if (searchComp) return searchComp.name;

        // Fall back to Redux state
        const competition = this.props.competitions[competitionId];
        return competition?.name || "Unknown Competition";
    };

    render() {
        const { isLoading, searchOptions, results, total, error } = this.state;
        const { selectedAssociation, competitions } = this.props;

        const hasFilters =
            searchOptions.name ||
            searchOptions.dayOfWeek !== undefined ||
            searchOptions.dateFrom ||
            searchOptions.dateTo ||
            searchOptions.seasonId ||
            searchOptions.competitionId;

        // Build competition options for select
        const competitionOptions = Object.values(competitions || {}).map((comp) => ({
            value: comp._id,
            label: comp.name,
        }));

        return (
            <AdminPageLayout
                title="Team Search"
                description="Search for teams by name, association, day of week, and more. Teams are prioritized by recent activity."
            >
                <Stack gap="md">
                    {/* Search Filters */}
                    <Card shadow="sm" padding="lg" radius="md" withBorder>
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Title order={3}>
                                    <FontAwesomeIcon icon={faFilter} /> Search Filters
                                </Title>
                                {hasFilters && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        leftSection={<FontAwesomeIcon icon={faTimes} />}
                                        onClick={this.clearFilters}
                                    >
                                        Clear Filters
                                    </Button>
                                )}
                            </Group>

                            <Group grow align="flex-start">
                                <TextInput
                                    label="Team Name"
                                    placeholder="Search by team name..."
                                    value={searchOptions.name || ""}
                                    onChange={(e) =>
                                        this.updateSearchOption("name", e.currentTarget.value)
                                    }
                                    leftSection={<FontAwesomeIcon icon={faSearch} />}
                                />

                                <Select
                                    label="Competition"
                                    placeholder="Filter by competition..."
                                    data={competitionOptions}
                                    value={searchOptions.competitionId || null}
                                    onChange={(value) =>
                                        this.updateSearchOption("competitionId", value || undefined)
                                    }
                                    clearable
                                    searchable
                                />
                            </Group>

                            <Group grow align="flex-start">
                                <Select
                                    label="Day of Week"
                                    placeholder="Filter by day..."
                                    data={DAYS_OF_WEEK}
                                    value={
                                        searchOptions.dayOfWeek !== undefined
                                            ? searchOptions.dayOfWeek.toString()
                                            : null
                                    }
                                    onChange={(value) =>
                                        this.updateSearchOption(
                                            "dayOfWeek",
                                            value ? parseInt(value) : undefined
                                        )
                                    }
                                    clearable
                                />

                                <div style={{ visibility: 'hidden', flexGrow: 1 }}>
                                    {/* Spacer for layout balance */}
                                </div>
                            </Group>

                            <Group grow align="flex-start">
                                <DateInput
                                    label="Games From"
                                    placeholder="Start date..."
                                    value={
                                        searchOptions.dateFrom
                                            ? new Date(searchOptions.dateFrom)
                                            : null
                                    }
                                    onChange={(value) =>
                                        this.updateSearchOption("dateFrom", value || undefined)
                                    }
                                    clearable
                                    leftSection={<FontAwesomeIcon icon={faCalendar} />}
                                />

                                <DateInput
                                    label="Games To"
                                    placeholder="End date..."
                                    value={
                                        searchOptions.dateTo
                                            ? new Date(searchOptions.dateTo)
                                            : null
                                    }
                                    onChange={(value) =>
                                        this.updateSearchOption("dateTo", value || undefined)
                                    }
                                    clearable
                                    leftSection={<FontAwesomeIcon icon={faCalendar} />}
                                />
                            </Group>

                            <Text size="xs" c="dimmed" ta="right">
                                Search updates automatically as you type
                            </Text>
                        </Stack>
                    </Card>

                    {/* Results Summary */}
                    {!isLoading && results.length > 0 && (
                        <Group>
                            <Text size="sm" c="dimmed">
                                Found {total} team{total !== 1 ? "s" : ""} matching your
                                criteria
                            </Text>
                            {selectedAssociation && (
                                <Badge color="blue">{selectedAssociation.name}</Badge>
                            )}
                        </Group>
                    )}

                    {/* Error Display */}
                    {error && (
                        <Card shadow="sm" padding="md" radius="md" withBorder bg="red.0">
                            <Text c="red" size="sm">
                                Error: {error}
                            </Text>
                        </Card>
                    )}

                    {/* Loading State */}
                    {isLoading && <CenteredLoader />}

                    {/* Results Table */}
                    {!isLoading && results.length > 0 && (
                        <Card shadow="sm" padding="lg" radius="md" withBorder>
                            <Stack gap="md">
                                <Title order={3}>Search Results</Title>
                                <div style={{ overflowX: "auto" }}>
                                    <Table striped highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Team Name</Table.Th>
                                                <Table.Th>Competition</Table.Th>
                                                <Table.Th>Total Games</Table.Th>
                                                <Table.Th>Last Game</Table.Th>
                                                <Table.Th>Next Game</Table.Th>
                                                <Table.Th>Recent Games</Table.Th>
                                                <Table.Th>Upcoming Games</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {results.map((team) => (
                                                <Table.Tr key={team._id}>
                                                    <Table.Td>
                                                        <Text fw={500}>{team.name}</Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {team.competitions && team.competitions.length > 0 ? (
                                                            <Text size="sm">
                                                                {this.getCompetitionName(team.competitions[0] as string)}
                                                            </Text>
                                                        ) : (
                                                            <Text size="sm" c="dimmed">-</Text>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Badge color="blue" variant="light">
                                                            {team.totalGames || 0}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm">
                                                            {this.formatDate(team.lastGameDate)}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm">
                                                            {this.formatDate(team.nextGameDate)}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Stack gap="xs">
                                                            {team.recentGames && team.recentGames.length > 0 ? (
                                                                team.recentGames.map((game, idx) => (
                                                                    <Tooltip
                                                                        key={idx}
                                                                        label={`${this.formatDateTime(
                                                                            game.date
                                                                        )} - ${game.competitionName || this.getCompetitionName(game.competition)} - ${this.getVenueName(
                                                                            game.venue
                                                                        )} (${game.court})`}
                                                                    >
                                                                        <Text size="xs" c="dimmed">
                                                                            {this.formatDate(game.date)} - vs{" "}
                                                                            {this.getTeamOpponent(game, team._id)}
                                                                        </Text>
                                                                    </Tooltip>
                                                                ))
                                                            ) : (
                                                                <Text size="xs" c="dimmed">
                                                                    No recent games
                                                                </Text>
                                                            )}
                                                        </Stack>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Stack gap="xs">
                                                            {team.upcomingGames &&
                                                                team.upcomingGames.length > 0 ? (
                                                                team.upcomingGames.map((game, idx) => (
                                                                    <Tooltip
                                                                        key={idx}
                                                                        label={`${this.formatDateTime(
                                                                            game.date
                                                                        )} - ${game.competitionName || this.getCompetitionName(game.competition)} - ${this.getVenueName(
                                                                            game.venue
                                                                        )} (${game.court})`}
                                                                    >
                                                                        <Text size="xs" c="green">
                                                                            {this.formatDate(game.date)} - vs{" "}
                                                                            {this.getTeamOpponent(game, team._id)}
                                                                        </Text>
                                                                    </Tooltip>
                                                                ))
                                                            ) : (
                                                                <Text size="xs" c="dimmed">
                                                                    No upcoming games
                                                                </Text>
                                                            )}
                                                        </Stack>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            </Stack>
                        </Card>
                    )}

                    {/* Pagination */}
                    {!isLoading && results.length > 0 && total > DEFAULT_PAGE_SIZE && (
                        <Group justify="center">
                            <Pagination
                                total={Math.ceil(total / (searchOptions.limit || DEFAULT_PAGE_SIZE))}
                                value={Math.floor((searchOptions.offset || 0) / (searchOptions.limit || DEFAULT_PAGE_SIZE)) + 1}
                                onChange={(page) => {
                                    const newOffset = (page - 1) * (searchOptions.limit || DEFAULT_PAGE_SIZE);
                                    this.setState(
                                        {
                                            searchOptions: {
                                                ...searchOptions,
                                                offset: newOffset,
                                            },
                                        },
                                        () => this.handleSearch()
                                    );
                                }}
                                size="md"
                            />
                            <Text size="sm" c="dimmed">
                                Page {Math.floor((searchOptions.offset || 0) / (searchOptions.limit || DEFAULT_PAGE_SIZE)) + 1} of{" "}
                                {Math.ceil(total / (searchOptions.limit || DEFAULT_PAGE_SIZE))}
                            </Text>
                        </Group>
                    )}

                    {/* No Results */}
                    {!isLoading && results.length === 0 && !error && (
                        <Card shadow="sm" padding="lg" radius="md" withBorder>
                            <Stack align="center" gap="md" py="xl">
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    size="3x"
                                    style={{ opacity: 0.3 }}
                                />
                                <Text size="lg" c="dimmed">
                                    No teams found matching your criteria
                                </Text>
                                <Text size="sm" c="dimmed">
                                    Try adjusting your search filters
                                </Text>
                            </Stack>
                        </Card>
                    )}
                </Stack>
            </AdminPageLayout>
        );
    }
}

const mapStateToProps = (state: IState) => ({
    associations: state.associations.map,
    competitions: state.competitions.map,
    seasons: {}, // state.seasons.map,
    venues: state.venues.map

});

export default connect(mapStateToProps)(TeamSearchPage);
